import { ApolloClient } from '@apollo/client';
import { USER_LIST_ITEM_QUERY } from './users.gql.tsx';

type UserRoleSummary = {
	roleId: number;
	role?: {
		roleId: number;
		name?: string | null;
		description?: string | null;
	} | null;
};

export interface UserListRecord {
	userId: string;
	username?: string | null;
	email?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	isActive: boolean;
	createdAt: string;
	userRoles?: UserRoleSummary[] | null;
}

interface UserListItemResponse {
	user?: UserListRecord | null;
}

export interface SelectUserAfterSaveOptions {
	userId: string;
	client: ApolloClient<any>;
	currentUsers: UserListRecord[];
	onDataUpdate: (updatedUsers: UserListRecord[]) => void;
	onUserSelect: (user: UserListRecord) => void;
}

/**
 * Fetches the latest user data from the server and updates the list,
 * then selects and highlights the user in the grid and details panel.
 * 
 * @param options Configuration options for selecting the user
 * @returns Promise that resolves when the operation is complete
 */
export async function selectUserAfterSave(
	options: SelectUserAfterSaveOptions
): Promise<void> {
	const { userId, client, currentUsers, onDataUpdate, onUserSelect } = options;

	try {
		// Step 1: Fetch the latest user data from the server
		const { data } = await client.query<UserListItemResponse>({
			query: USER_LIST_ITEM_QUERY,
			variables: { id: userId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedUser = data?.user;

		if (!fetchedUser) {
			console.warn(`User with ID ${userId} not found after save`);
			return;
		}

		// Step 2: Check if user exists in current list
		const existingUserIndex = currentUsers.findIndex(
			u => u.userId === userId
		);

		let updatedUsers: UserListRecord[];

		if (existingUserIndex >= 0) {
			// User exists - replace with updated data
			updatedUsers = [...currentUsers];
			updatedUsers[existingUserIndex] = fetchedUser;
		} else {
			// New user - append to the list
			updatedUsers = [...currentUsers, fetchedUser];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedUsers);

		// Step 4: Select the user (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onUserSelect(fetchedUser);
		}, 50);
	} catch (error) {
		console.error('Error selecting user after save:', error);
		throw error;
	}
}